import {
  YES_NO,
  applicantSection,
  feeInfo,
  otherAttachments,
  penaltyDeclaration,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A5. Wniosek o podział nieruchomości — art. 92–99 ugn (zwł. art. 93 ust. 4–5 — postanowienie opiniujące wstępny projekt,
 * art. 95 — podział niezależnie od planu, art. 96 ust. 1 — decyzja zatwierdzająca, art. 97 ust. 1a — załączniki);
 * rozporządzenie RM z 7.12.2004 r. w sprawie sposobu i trybu dokonywania podziałów nieruchomości. Brak wzoru
 * ogólnopolskiego; układ jak wzory Radomia (https://bip.radom.pl/download/69/18877/wniosekpodzial.pdf — tryb art. 93 /
 * art. 95, cel, dostęp do drogi, załączniki etapów) i Raszyna (etap I), oba pobrane i odczytane 22.10.2026.
 * Postępowanie dwuetapowe → wariant w BE: etap I (zaopiniowanie wstępnego projektu) albo zatwierdzenie podziału
 * (po postanowieniu — z operatem geodezyjnym, albo od razu w trybie art. 95). Oświadczenie o stanie wpisów w KW
 * (art. 116 ust. 2 pkt 4 i ust. 2a ugn) z klauzulą o odpowiedzialności karnej. Pełnomocnictwo bez opłaty skarbowej —
 * według katalogu (sprawa z ugn, art. 2 ust. 1 pkt 1 lit. h uos) — do potwierdzenia z wydziałem.
 */
const PRELIMINARY: EbiCondition = { field: "etap", equals: "wstepny" };
const APPROVAL: EbiCondition = { field: "etap", equals: "zatwierdzenie" };
const ART_95: EbiCondition = { all: [APPROVAL, { field: "tryb", equals: "art95" }] };
const ART_93: EbiCondition = { any: [PRELIMINARY, { all: [APPROVAL, { field: "tryb", equals: "art93" }] }] };
const STATEMENT: EbiCondition = { field: "tytulSposob", equals: "oswiadczenie" };
const CO_OWNERS: EbiCondition = { field: "wspolwlasciciele", equals: "tak" };

const representation = representationSection();

const A5: IEbiFormDefinition = {
  code: "A5",
  version: 1,
  title: "Wniosek o podział nieruchomości",
  category: EEbiFormCategory.Property,
  office: EEbiFormOffice.Municipality,
  description:
    "Zaopiniowanie wstępnego projektu podziału nieruchomości (etap I) i decyzja zatwierdzająca podział — także w trybie art. 95 ugn, niezależnie od planu.",
  keywords: ["podział nieruchomości", "podział działki", "wstępny projekt podziału", "decyzja zatwierdzająca podział", "geodeta", "działka"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis:
    "art. 93, art. 94, art. 95, art. 96 ust. 1 i art. 97 ust. 1a ustawy z dnia 21 sierpnia 1997 r. o gospodarce nieruchomościami; rozporządzenie Rady Ministrów z dnia 7 grudnia 2004 r. w sprawie sposobu i trybu dokonywania podziałów nieruchomości",
  request: "Wnoszę o dokonanie podziału nieruchomości opisanej poniżej w zakresie wskazanym we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty skarbowej — sprawa z ustawy o gospodarce nieruchomościami (art. 2 ust. 1 pkt 1 lit. h ustawy o opłacie skarbowej). Koszty prac geodezyjnych ponosi wnioskodawca.",
  deadlineLabel: "do miesiąca",
  deadline: "Według KPA — do miesiąca, w sprawie szczególnie skomplikowanej do 2 miesięcy (etap I i etap decyzji osobno).",
  variantField: "etap",
  variants: {
    wstepny: { title: "Wniosek o zaopiniowanie wstępnego projektu podziału nieruchomości" },
    zatwierdzenie: { title: "Wniosek o zatwierdzenie podziału nieruchomości" },
  },
  sections: [
    applicantSection(),
    {
      ...representation,
      fields: representation.fields.filter((field) => field.code !== "pelnomocnictwoZwolnione"),
    },
    {
      code: "wnioskodawcy",
      title: "Współwłaściciele",
      description: "Wniosek o podział nieruchomości będącej przedmiotem współwłasności podpisują wszyscy współwłaściciele.",
      fields: [
        {
          code: "wspolwlasciciele",
          type: "radio",
          label: "Czy nieruchomość ma innych współwłaścicieli lub współużytkowników wieczystych?",
          required: true,
          options: YES_NO,
        },
        {
          code: "pozostaliWspolwlasciciele",
          type: "repeat",
          label: "Pozostali współwłaściciele lub współużytkownicy wieczyści",
          itemLabel: "Współwłaściciel",
          required: true,
          visibleWhen: CO_OWNERS,
          min: 1,
          max: 30,
          fields: [
            { code: "nazwa", type: "text", label: "Imię i nazwisko albo nazwa", required: true, maxLength: 300 },
            { code: "adres", type: "text", label: "Adres zamieszkania albo siedziby", required: true, maxLength: 300 },
          ],
        },
      ],
    },
    {
      code: "nieruchomosc",
      title: "Nieruchomość",
      fields: [
        { code: "polozenie", type: "text", label: "Położenie — miejscowość i ulica", required: true, maxLength: 300 },
        {
          code: "dzialki",
          type: "repeat",
          label: "Działki ewidencyjne objęte podziałem",
          itemLabel: "Działka",
          required: true,
          min: 1,
          max: 20,
          fields: [
            { code: "obreb", type: "text", label: "Obręb ewidencyjny (nazwa lub numer)", required: true, width: "half", maxLength: 100 },
            { code: "numer", type: "text", label: "Numer działki", required: true, width: "half", maxLength: 50, placeholder: "np. 125/4" },
            { code: "arkusz", type: "text", label: "Arkusz mapy (jeśli występuje)", width: "third", maxLength: 20 },
            { code: "powierzchnia", type: "number", label: "Powierzchnia [ha]", decimals: 4, min: 0, width: "third" },
            { code: "kw", type: "text", label: "Numer księgi wieczystej (jeśli założona)", width: "third", maxLength: 30, placeholder: "np. LU1I/00012345/6" },
          ],
        },
        {
          code: "zabytek",
          type: "radio",
          label: "Czy nieruchomość jest wpisana do rejestru zabytków?",
          required: true,
          options: YES_NO,
        },
      ],
    },
    {
      code: "podzial",
      title: "Podział",
      fields: [
        {
          code: "etap",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            {
              value: "wstepny",
              label:
                "zaopiniowanie wstępnego projektu podziału (etap I) — postanowienie o zgodności z ustaleniami planu miejscowego, a w razie jego braku z decyzją o warunkach zabudowy lub przepisami odrębnymi",
            },
            { value: "zatwierdzenie", label: "zatwierdzenie podziału nieruchomości decyzją (po postanowieniu opiniującym albo w trybie art. 95 ugn)" },
          ],
        },
        {
          code: "tryb",
          type: "radio",
          label: "Podział następuje",
          required: true,
          visibleWhen: APPROVAL,
          options: [
            {
              value: "art93",
              label: "zgodnie z ustaleniami planu miejscowego, a w razie jego braku z decyzją o warunkach zabudowy lub przepisami odrębnymi (wstępny projekt został zaopiniowany)",
            },
            {
              value: "art95",
              label: "zgodnie z załączoną mapą z projektem podziału w trybie art. 95 ugn — niezależnie od ustaleń planu miejscowego i od decyzji o warunkach zabudowy",
            },
          ],
        },
        {
          code: "postanowienie",
          type: "text",
          label: "Znak i data postanowienia opiniującego wstępny projekt podziału",
          required: true,
          visibleWhen: { all: [APPROVAL, { field: "tryb", equals: "art93" }] },
          maxLength: 200,
        },
        {
          code: "przeslankaArt95",
          type: "text",
          label: "Przesłanka podziału z art. 95 ugn (punkt przepisu i krótki opis)",
          required: true,
          visibleWhen: ART_95,
          maxLength: 500,
        },
        {
          code: "podstawaZgodnosci",
          type: "radio",
          label: "Zgodność podziału ocenia się według",
          required: true,
          visibleWhen: ART_93,
          options: [
            { value: "mpzp", label: "ustaleń miejscowego planu zagospodarowania przestrzennego" },
            { value: "wz", label: "decyzji o warunkach zabudowy (brak planu, decyzja wydana przed złożeniem wniosku)" },
            { value: "przepisyOdrebne", label: "przepisów odrębnych (brak planu i decyzji o warunkach zabudowy)" },
          ],
        },
        { code: "cel", type: "textarea", label: "Podział ma na celu", required: true, maxLength: 2000 },
        {
          code: "dostepDrogi",
          type: "textarea",
          label: "Przewidywany dostęp do drogi publicznej dla wydzielanych działek",
          required: true,
          maxLength: 1000,
          hint: "Nazwa drogi publicznej, a przy drodze wewnętrznej — nazwa ulicy lub numery działek stanowiących drogę dojazdową.",
        },
        {
          code: "synchronizacja",
          type: "radio",
          label: "Czy oznaczenie działek w księdze wieczystej jest inne niż w ewidencji gruntów i budynków?",
          required: true,
          visibleWhen: APPROVAL,
          options: YES_NO,
        },
        feeInfo(
          "infoOplata",
          "Decyzja wydawana na podstawie ustawy o gospodarce nieruchomościami nie podlega opłacie skarbowej (art. 2 ust. 1 pkt 1 lit. h ustawy o opłacie skarbowej). Koszty podziału, w tym prac geodezyjnych, ponosi wnioskodawca.",
        ),
      ],
    },
    {
      code: "tytulPrawny",
      title: "Tytuł prawny do nieruchomości",
      fields: [
        {
          code: "tytulSposob",
          type: "radio",
          label: "Tytuł prawny wykazuję",
          required: true,
          defaultValue: "dokument",
          options: [
            { value: "dokument", label: "dokumentem (odpis z księgi wieczystej, akt notarialny, postanowienie sądu) — dołączam go" },
            {
              value: "oswiadczenie",
              label: "oświadczeniem o aktualnym stanie wpisów w księdze wieczystej (art. 116 ust. 2 pkt 4 i ust. 2a ustawy o gospodarce nieruchomościami)",
            },
          ],
        },
        {
          code: "stanWpisow",
          type: "textarea",
          label: "Oświadczam, że aktualny stan wpisów w księdze wieczystej (numer księgi, oznaczenie nieruchomości, właściciele lub użytkownicy wieczyści) jest następujący",
          required: true,
          visibleWhen: STATEMENT,
          maxLength: 3000,
        },
        { ...penaltyDeclaration(), visibleWhen: STATEMENT },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "tytulDokument",
      label: "Dokument stwierdzający tytuł prawny do nieruchomości (odpis lub wydruk z księgi wieczystej)",
      visibleWhen: { not: STATEMENT },
      required: true,
    },
    {
      code: "wypisKataster",
      label: "Wypis z katastru nieruchomości (ewidencji gruntów i budynków) i kopia mapy katastralnej obejmującej nieruchomość",
      hint: "Dokumenty z powiatowego zasobu geodezyjnego (PODGiK).",
      required: true,
    },
    {
      code: "decyzjaWz",
      label: "Decyzja o warunkach zabudowy wraz z załącznikiem graficznym",
      visibleWhen: { all: [ART_93, { field: "podstawaZgodnosci", equals: "wz" }] },
      required: true,
    },
    {
      code: "pozwolenieKonserwatora",
      label: "Pozwolenie wojewódzkiego konserwatora zabytków na podział (art. 96 ust. 1a ugn)",
      visibleWhen: { field: "zabytek", equals: "tak" },
      required: true,
    },
    {
      code: "wstepnyProjekt",
      label: "Wstępny projekt podziału sporządzony na kopii mapy zasadniczej",
      hint: "Według rozporządzenia w sprawie sposobu i trybu dokonywania podziałów nieruchomości; urzędy zwykle proszą też o egzemplarze papierowe.",
      visibleWhen: PRELIMINARY,
      required: true,
    },
    {
      code: "protokolGranic",
      label: "Protokół z przyjęcia granic nieruchomości",
      visibleWhen: APPROVAL,
      required: true,
    },
    {
      code: "wykazZmian",
      label: "Wykaz zmian gruntowych",
      visibleWhen: APPROVAL,
      required: true,
    },
    {
      code: "wykazSynchronizacyjny",
      label: "Wykaz synchronizacyjny",
      hint: "Gdy oznaczenie działek w księdze wieczystej jest inne niż w ewidencji gruntów i budynków.",
      visibleWhen: { all: [APPROVAL, { field: "synchronizacja", equals: "tak" }] },
      required: true,
    },
    {
      code: "mapaPodzialu",
      label: "Mapa z projektem podziału",
      hint: "Przyjęta do państwowego zasobu geodezyjnego i kartograficznego (PDF podpisany przez geodetę).",
      visibleWhen: APPROVAL,
      required: true,
    },
    {
      code: "celArt95",
      label: "Dokumenty potwierdzające cel podziału w trybie art. 95 ugn",
      hint: "Praktyka urzędów — nieobowiązkowo.",
      visibleWhen: ART_95,
      multiple: true,
    },
    {
      code: "zgodaWspolwlascicieli",
      label: "Wniosek podpisany przez pozostałych współwłaścicieli albo ich pełnomocnictwa",
      visibleWhen: CO_OWNERS,
      required: true,
      multiple: true,
    },
    {
      code: "krs",
      label: "Odpis z KRS — gdy właścicielem jest osoba prawna",
      hint: "Praktyka urzędów — nieobowiązkowo.",
    },
    {
      code: "opiniaZjazd",
      label: "Opinia zarządcy drogi o możliwości lokalizacji zjazdu",
      hint: "Praktyka urzędów — nieobowiązkowo.",
    },
    ...proxyAttachments().filter((attachment) => attachment.code !== "oplataPelnomocnictwo"),
    otherAttachments(),
  ],
};

export default A5;
